import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Mail, X, ShoppingBag, ChefHat, Truck, Settings, Star, Package, MapPin, Phone, CircleCheck as CheckCircle, TriangleAlert as AlertTriangle, Activity, LocationEdit as Edit2, Save, Camera, Loader, UtensilsCrossed, TrendingUp, Clock, RefreshCw, IndianRupee, Plus, Trash2, Navigation } from 'lucide-react';
import { INVENTORY_SUMMARY_URL, GET_SAVED_ADDRESSES_URL, SAVE_ADDRESS_URL, SET_ACTIVE_ADDRESS_URL, DELETE_ADDRESS_URL, UPDATE_CURRENT_LOCATION_URL } from '../../config/api';
import { getCurrentLocationDetails } from '../Utils/location';
import '../UserKitchenManager/UserKitchenManager.css';
import UserAccountDeletion from '../UserAccountDeletion/UserAccountDeletion';
import UserKitchenManager from '../UserKitchenManager/UserKitchenManager';
import UserPlacedOrdersTab from './UserPlacedOrdersTab';
import MyActiveOrdersTab from './MyActiveOrdersTab';
import MyKitchenOrdersTab from '../UserKitchenManager/MyKitchenOrdersTab';
import './UserProfile.css';

interface UserProfileProps {
  onLogout: () => void;
  userRoles?: string[];
}

interface ProfileData {
  user_id: number;
  email: string;
  is_verified: boolean;
  profile_completed: boolean;
  onboarding_step: number;
  country: string | null;
  role: string[];
  is_blocked: boolean;
  city: string | null;
  last_active: string | null;
  name: string | null;
  phone: string | null;
  phone_number: string | null;
  profile_photo: string | null;
  gender: string | null;
  dob: string | null;
  bio: string | null;
  state: string | null;
  pincode: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  food_preferences: string | null;
  dietary_preference: string | null;
  favorite_cuisine: string | null;
  kitchen_id: number | null;
}

interface DashboardData {
  success: boolean;
  kitchen: {
    kitchen_id: number;
    kitchen_name: string;
    status: boolean;
    is_approved: boolean;
  };
  analytics: {
    total_orders: number;
    pending_orders: number;
    completed_orders: number;
    cancelled_orders: number;
    total_revenue: number;
    today_revenue: number;
    monthly_revenue: number;
    total_food_items: number;
  };
  top_selling_foods: Array<{ food_name: string; total_sold: number }>;
  low_stock_foods: Array<{ food_id: number; food_name: string; remaining_quantity: number; image?: string; is_available?: boolean }>;
}

const ROLE_LABELS: Record<string, string> = {
  customer: 'Customer',
  kitchen_owner: 'Kitchen Owner',
  delivery_partner: 'Delivery Partner',
};

interface SavedAddress {
  address_id: string;
  label: string;
  address_line: string;
  city: string;
  state: string;
  pincode: string;
  latitude: number;
  longitude: number;
  is_default: boolean;
  created_at?: string;
}

interface AddressForm {
  label: string;
  address_line: string;
  city: string;
  state: string;
  pincode: string;
  latitude: string;
  longitude: string;
  is_default: boolean;
}

const UserProfile: React.FC<UserProfileProps> = ({ onLogout, userRoles: propRoles }) => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'profile');
  const [showAccountDeletionModal, setShowAccountDeletionModal] = useState(false);
  const [showStatusToast, setShowStatusToast] = useState(false);
  const [toastStatus] = useState<'online' | 'offline'>('online');
  const [kitchenId, setKitchenId] = useState<number | null>(null);

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  const [inventorySummary, setInventorySummary] = useState<{
    total_products: number;
    available_products: number;
    low_stock_products: number;
    sold_out_products: number;
  } | null>(null);

  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const userEmail = localStorage.getItem('userEmail') || '';
  const token = localStorage.getItem('authToken') || localStorage.getItem('token') || '';

  // Address management state
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [activeAddressId, setActiveAddressId] = useState<string | null>(null);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState<AddressForm>({
    label: 'Home', address_line: '', city: '', state: '', pincode: '',
    latitude: '', longitude: '', is_default: false,
  });
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [isDetectingAddrLocation, setIsDetectingAddrLocation] = useState(false);

  // Derive effective roles: prefer API profile data once loaded, else fall back to props/localStorage
  const effectiveRoles: string[] = profileData?.role ?? propRoles ?? [];
  const isCustomer = effectiveRoles.includes('customer');
  const isKitchenOwner = effectiveRoles.includes('kitchen_owner');
  const isDeliveryPartner = effectiveRoles.includes('delivery_partner');

  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>>([]);

  const showNotification = (
    type: 'success' | 'error' | 'info' | 'warning',
    title: string,
    message: string,
  ) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, type, title, message }]);
    setTimeout(() => removeNotification(id), 5000);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const fetchProfile = async () => {
    setProfileLoading(true);
    setProfileError('');
    try {
      const res = await fetch('https://api.homecookt.com/api/v1/users/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (res.ok && json.success) {
        const data: ProfileData = json.data;
        setProfileData({ ...data, phone: data.phone_number ?? data.phone });
        if (data.kitchen_id) {
          setKitchenId(data.kitchen_id);
          localStorage.setItem('kitchenId', String(data.kitchen_id));
        }
      } else {
        setProfileError('Failed to load profile. Please try again.');
      }
    } catch {
      setProfileError('Network error. Please check your connection.');
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchDashboard = async (resolvedKitchenId?: number | null) => {
    const id = resolvedKitchenId !== undefined ? resolvedKitchenId : kitchenId;
    if (!id) return;
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const res = await fetch('https://api.homecookt.com/api/v1/kitchen-dashboard', {
        headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
      });
      if (res.ok) {
        setDashboardData(await res.json());
      } else if (res.status === 401) {
        setDashboardError('Session expired. Please log in again.');
      } else if (res.status === 404) {
        setDashboardData(null);
      } else {
        setDashboardError('Failed to load dashboard. Please try again.');
      }
    } catch {
      setDashboardError('Network error. Please check your connection.');
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchInventorySummary = async () => {
    try {
      const res = await fetch(INVENTORY_SUMMARY_URL, {
        headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setInventorySummary({
          total_products: data.total_products ?? 0,
          available_products: data.available_products ?? 0,
          low_stock_products: data.low_stock_products ?? 0,
          sold_out_products: data.sold_out_products ?? 0,
        });
      }
    } catch {
      // non-critical — silently ignore
    }
  };

  const fetchAddresses = async () => {
    setAddressesLoading(true);
    try {
      const res = await fetch(GET_SAVED_ADDRESSES_URL, {
        headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
      });
      if (res.ok) {
        const json = await res.json();
        setSavedAddresses(json.saved_addresses ?? []);
        setActiveAddressId(json.active_address?.address_id ?? null);
        if (json.current_location?.city) {
          localStorage.setItem('activeCity', json.current_location.city);
        }
      }
    } catch {
      // non-critical
    } finally {
      setAddressesLoading(false);
    }
  };

  const handleDetectAddrLocation = async () => {
    setIsDetectingAddrLocation(true);
    try {
      const result = await getCurrentLocationDetails();
      if ('type' in result) return;
      setAddressForm(p => ({
        ...p,
        address_line: result.full_address || p.address_line,
        city: result.city || p.city,
        state: result.state || p.state,
        pincode: result.postal_code || p.pincode,
        latitude: result.latitude != null ? String(result.latitude) : p.latitude,
        longitude: result.longitude != null ? String(result.longitude) : p.longitude,
      }));
    } catch {
      // non-critical
    } finally {
      setIsDetectingAddrLocation(false);
    }
  };

  const handleSaveAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddressSaving(true);
    setAddressError('');
    try {
      const lat = parseFloat(addressForm.latitude);
      const lon = parseFloat(addressForm.longitude);
      const body = {
        label: addressForm.label,
        address_line: addressForm.address_line,
        city: addressForm.city,
        state: addressForm.state,
        pincode: addressForm.pincode,
        latitude: isNaN(lat) ? 0 : lat,
        longitude: isNaN(lon) ? 0 : lon,
        is_default: addressForm.is_default,
      };
      const res = await fetch(SAVE_ADDRESS_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setShowAddressForm(false);
        setAddressForm({ label: 'Home', address_line: '', city: '', state: '', pincode: '', latitude: '', longitude: '', is_default: false });
        await fetchAddresses();
        showNotification('success', 'Address Saved', 'Your address has been added.');

        // Also update current location on backend if coords given
        if (!isNaN(lat) && !isNaN(lon)) {
          try {
            await fetch(UPDATE_CURRENT_LOCATION_URL(lat, lon, addressForm.address_line || undefined), {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
            });
          } catch { /* non-critical */ }
        }
      } else {
        setAddressError(json.message || 'Could not save address. Please try again.');
      }
    } catch {
      setAddressError('Network error. Please try again.');
    } finally {
      setAddressSaving(false);
    }
  };

  const handleSetActiveAddress = async (addressId: string) => {
    try {
      const res = await fetch(SET_ACTIVE_ADDRESS_URL(addressId), {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setActiveAddressId(addressId);
        if (json.active_address?.city) localStorage.setItem('activeCity', json.active_address.city);
        window.dispatchEvent(new Event('activeCityChanged'));
        showNotification('success', 'Address Updated', `${json.active_address?.label ?? 'Address'} set as your active location.`);
      }
    } catch { /* non-critical */ }
  };

  const handleDeleteAddress = async (addressId: string) => {
    try {
      const res = await fetch(DELETE_ADDRESS_URL(addressId), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setSavedAddresses(prev => prev.filter(a => a.address_id !== addressId));
        if (activeAddressId === addressId) setActiveAddressId(null);
        showNotification('success', 'Address Removed', 'The address has been deleted.');
      }
    } catch { /* non-critical */ }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isKitchenOwner && kitchenId) {
      fetchDashboard(kitchenId);
      fetchInventorySummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKitchenOwner, kitchenId]);

  useEffect(() => {
    const storedKitchenId = localStorage.getItem('kitchenId');
    if (storedKitchenId) setKitchenId(parseInt(storedKitchenId, 10));
  }, []);

  useEffect(() => {
    const handleKitchenCreated = (event: any) => {
      if (event.detail?.kitchenId) {
        setKitchenId(event.detail.kitchenId);
        localStorage.setItem('kitchenId', event.detail.kitchenId.toString());
      }
    };
    const handleKitchenDeleted = () => {
      setKitchenId(null);
      localStorage.removeItem('kitchenId');
    };
    window.addEventListener('kitchenCreated', handleKitchenCreated);
    window.addEventListener('kitchenDeleted', handleKitchenDeleted);
    return () => {
      window.removeEventListener('kitchenCreated', handleKitchenCreated);
      window.removeEventListener('kitchenDeleted', handleKitchenDeleted);
    };
  }, []);

  useEffect(() => {
    if (showAccountDeletionModal) setShowAccountDeletionModal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'addresses') fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const startEditing = () => {
    if (!profileData) return;
    setEditForm({
      name: profileData.name || '',
      phone: profileData.phone || '',
      gender: profileData.gender || '',
      dob: profileData.dob || '',
      bio: profileData.bio || '',
      country: profileData.country || '',
      state: profileData.state || '',
      city: profileData.city || '',
      pincode: profileData.pincode || '',
      address: profileData.address || '',
      latitude: profileData.latitude != null ? String(profileData.latitude) : '',
      longitude: profileData.longitude != null ? String(profileData.longitude) : '',
      food_preferences: profileData.food_preferences || '',
      dietary_preference: profileData.dietary_preference || '',
      favorite_cuisine: profileData.favorite_cuisine || '',
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleEditFieldChange = (key: string, value: string) => {
    setEditForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const form = new FormData();
      Object.entries(editForm).forEach(([key, val]) => {
        const fieldName = key === 'phone' ? 'phone_number' : key;
        if (key === 'latitude' || key === 'longitude') {
          const num = parseFloat(val);
          if (!isNaN(num)) form.append(fieldName, String(num));
        } else if (val !== '') {
          form.append(fieldName, val);
        }
      });
      if (photoFile) form.append('profile_photo', photoFile);

      const res = await fetch('https://api.homecookt.com/api/v1/users/profile', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setProfileData({ ...json.data, phone: json.data.phone_number ?? json.data.phone });
        if (json.data.name) localStorage.setItem('userName', json.data.name);
        setIsEditing(false);
        setPhotoFile(null);
        setPhotoPreview(null);
        showNotification('success', 'Profile Updated', 'Your profile has been saved successfully.');
      } else {
        showNotification('error', 'Update Failed', json.message || 'Could not update profile. Please try again.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const displayName = profileData?.name || userEmail.split('@')[0] || 'User';
  const avatarSrc = photoPreview || profileData?.profile_photo || null;

  return (
    <div className="user-profile-page">
      <div className="user-profile-container">
        {/* Profile Header */}
        <div className="user-profile-header">
          <div className="user-profile-image-section">
            <div className="user-profile-image-wrapper">
              <div className="user-profile-image-placeholder">
                {avatarSrc ? (
                  <img src={avatarSrc} alt="Profile" className="user-profile-avatar-img" />
                ) : (
                  <User size={60} />
                )}
              </div>
            </div>
          </div>

          <div className="user-profile-info">
            <div className="user-profile-name-section">
              <h1 className="user-profile-name">
                {profileLoading ? 'Loading...' : displayName}
              </h1>
              <div className="user-profile-rating">
                <Star size={20} fill="currentColor" />
                <span>4.8</span>
                <span className="user-profile-review-count">(24 reviews)</span>
              </div>
              {profileData && (profileData.role ?? []).length > 0 && (
                <div className="user-profile-role-badges">
                  {(profileData.role ?? []).map(r => (
                    <span key={r} className="user-profile-role-badge">
                      {ROLE_LABELS[r] || r}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="user-profile-contact">
              <div className="user-profile-contact-item">
                <Mail size={20} />
                <span>{profileData?.email || userEmail}</span>
              </div>
              {profileData?.phone && (
                <div className="user-profile-contact-item">
                  <Phone size={20} />
                  <span>{profileData.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Kitchen Dashboard — shown only for kitchen owners who have a kitchen */}
        {isKitchenOwner && kitchenId && (
          <div className="kitchen-dashboard-container" style={{ marginBottom: '1.5rem' }}>
            <div className="kitchen-dashboard-header">
              <div>
                <h4>Kitchen Dashboard</h4>
                <p>Overview of your kitchen performance</p>
              </div>
              <button
                className="user-profile-edit-btn"
                onClick={() => { fetchDashboard(kitchenId); fetchInventorySummary(); }}
                disabled={dashboardLoading}
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>

            {dashboardLoading && (
              <div className="user-profile-empty-state" style={{ padding: '2rem' }}>
                <div className="user-profile-loading-spinner" style={{ width: '36px', height: '36px', marginBottom: '0.75rem' }}></div>
                <p>Loading dashboard...</p>
              </div>
            )}

            {dashboardError && !dashboardLoading && (
              <div className="user-profile-empty-state" style={{ padding: '2rem' }}>
                <AlertTriangle size={36} />
                <p>{dashboardError}</p>
                <button className="user-profile-edit-btn" onClick={() => fetchDashboard(kitchenId)} style={{ marginTop: '1rem' }}>
                  <RefreshCw size={16} />
                  Try Again
                </button>
              </div>
            )}

            {!dashboardLoading && !dashboardError && dashboardData && (
              <>
                <div className="dashboard-stats-grid">
                  <div className="dashboard-stat-card">
                    <div className="dashboard-stat-icon orders-icon"><ShoppingBag size={20} /></div>
                    <div className="dashboard-stat-content">
                      <span className="dashboard-stat-value">{dashboardData.analytics.total_orders}</span>
                      <span className="dashboard-stat-label">Total Orders</span>
                    </div>
                  </div>
                  <div className="dashboard-stat-card">
                    <div className="dashboard-stat-icon pending-icon"><Clock size={20} /></div>
                    <div className="dashboard-stat-content">
                      <span className="dashboard-stat-value">{dashboardData.analytics.pending_orders}</span>
                      <span className="dashboard-stat-label">Pending</span>
                    </div>
                  </div>
                  <div className="dashboard-stat-card">
                    <div className="dashboard-stat-icon completed-icon"><CheckCircle size={20} /></div>
                    <div className="dashboard-stat-content">
                      <span className="dashboard-stat-value">{dashboardData.analytics.completed_orders}</span>
                      <span className="dashboard-stat-label">Completed</span>
                    </div>
                  </div>
                  <div className="dashboard-stat-card">
                    <div className="dashboard-stat-icon cancelled-icon"><X size={20} /></div>
                    <div className="dashboard-stat-content">
                      <span className="dashboard-stat-value">{dashboardData.analytics.cancelled_orders}</span>
                      <span className="dashboard-stat-label">Cancelled</span>
                    </div>
                  </div>
                  <div className="dashboard-stat-card">
                    <div className="dashboard-stat-icon revenue-icon"><IndianRupee size={20} /></div>
                    <div className="dashboard-stat-content">
                      <span className="dashboard-stat-value">₹{dashboardData.analytics.total_revenue.toFixed(2)}</span>
                      <span className="dashboard-stat-label">Total Revenue</span>
                    </div>
                  </div>
                  <div className="dashboard-stat-card">
                    <div className="dashboard-stat-icon today-icon"><TrendingUp size={20} /></div>
                    <div className="dashboard-stat-content">
                      <span className="dashboard-stat-value">₹{dashboardData.analytics.today_revenue.toFixed(2)}</span>
                      <span className="dashboard-stat-label">Today's Revenue</span>
                    </div>
                  </div>
                  <div className="dashboard-stat-card">
                    <div className="dashboard-stat-icon monthly-icon"><IndianRupee size={20} /></div>
                    <div className="dashboard-stat-content">
                      <span className="dashboard-stat-value">₹{dashboardData.analytics.monthly_revenue.toFixed(2)}</span>
                      <span className="dashboard-stat-label">Monthly Revenue</span>
                    </div>
                  </div>
                  <div className="dashboard-stat-card">
                    <div className="dashboard-stat-icon food-icon"><UtensilsCrossed size={20} /></div>
                    <div className="dashboard-stat-content">
                      <span className="dashboard-stat-value">{dashboardData.analytics.total_food_items}</span>
                      <span className="dashboard-stat-label">Food Items</span>
                    </div>
                  </div>
                </div>

                {/* Inventory Summary Strip */}
                {inventorySummary && (
                  <div className="inventory-summary-strip">
                    <div className={`inv-chip inv-chip--total`}>
                      <span className="inv-chip-value">{inventorySummary.total_products}</span>
                      <span className="inv-chip-label">Total Products</span>
                    </div>
                    <div className="inv-chip inv-chip--available">
                      <span className="inv-chip-value">{inventorySummary.available_products}</span>
                      <span className="inv-chip-label">Available</span>
                    </div>
                    <div className={`inv-chip ${inventorySummary.low_stock_products > 0 ? 'inv-chip--warn' : 'inv-chip--ok'}`}>
                      <span className="inv-chip-value">{inventorySummary.low_stock_products}</span>
                      <span className="inv-chip-label">Low Stock</span>
                    </div>
                    <div className={`inv-chip ${inventorySummary.sold_out_products > 0 ? 'inv-chip--danger' : 'inv-chip--ok'}`}>
                      <span className="inv-chip-value">{inventorySummary.sold_out_products}</span>
                      <span className="inv-chip-label">Sold Out</span>
                    </div>
                  </div>
                )}

                <div className="dashboard-panels-row">
                  <div className="dashboard-panel">
                    <h5 className="dashboard-panel-title">
                      <TrendingUp size={16} />
                      Top Selling Foods
                    </h5>
                    {(dashboardData.top_selling_foods ?? []).length === 0 ? (
                      <p className="dashboard-panel-empty">No sales data yet.</p>
                    ) : (
                      <ul className="dashboard-panel-list">
                        {(dashboardData.top_selling_foods ?? []).map((food, idx) => (
                          <li key={idx} className="dashboard-panel-list-item">
                            <span className="dpl-rank">#{idx + 1}</span>
                            <span className="dpl-name">{food.food_name}</span>
                            <span className="dpl-value">{food.total_sold} sold</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="dashboard-panel">
                    <h5 className="dashboard-panel-title">
                      <AlertTriangle size={16} />
                      Low Stock Items
                    </h5>
                    {(dashboardData.low_stock_foods ?? []).length === 0 ? (
                      <p className="dashboard-panel-empty">All items are well stocked.</p>
                    ) : (
                      <ul className="dashboard-panel-list">
                        {(dashboardData.low_stock_foods ?? []).map((food) => (
                          <li key={food.food_id} className="dashboard-panel-list-item">
                            <span className="dpl-name">{food.food_name}</span>
                            <span className="dpl-value low-stock-value">{food.remaining_quantity} left</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="user-profile-tabs">
          <div className="user-profile-tabs-container">
            <button
              className={`user-profile-tab-button ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <User className="user-profile-tab-icon" size={20} />
              <span className="user-profile-tab-text">Profile</span>
            </button>
            {(isCustomer || isDeliveryPartner) && (
              <button
                className={`user-profile-tab-button ${activeTab === 'active-orders' ? 'active' : ''}`}
                onClick={() => setActiveTab('active-orders')}
              >
                <Activity className="user-profile-tab-icon" size={20} />
                <span className="user-profile-tab-text">Active Orders</span>
              </button>
            )}
            {isCustomer && (
              <button
                className={`user-profile-tab-button ${activeTab === 'my-orders' ? 'active' : ''}`}
                onClick={() => setActiveTab('my-orders')}
              >
                <ShoppingBag className="user-profile-tab-icon" size={20} />
                <span className="user-profile-tab-text">Order History</span>
              </button>
            )}
            {isKitchenOwner && (
              <button
                className={`user-profile-tab-button ${activeTab === 'kitchen' ? 'active' : ''}`}
                onClick={() => setActiveTab('kitchen')}
              >
                <ChefHat className="user-profile-tab-icon" size={20} />
                <span className="user-profile-tab-text">My Kitchen</span>
              </button>
            )}
            {isKitchenOwner && kitchenId && (
              <button
                className={`user-profile-tab-button ${activeTab === 'kitchen-orders' ? 'active' : ''}`}
                onClick={() => setActiveTab('kitchen-orders')}
              >
                <Package className="user-profile-tab-icon" size={20} />
                <span className="user-profile-tab-text">Kitchen Orders</span>
              </button>
            )}
            {isDeliveryPartner && (
              <button
                className={`user-profile-tab-button ${activeTab === 'my-deliveries' ? 'active' : ''}`}
                onClick={() => setActiveTab('my-deliveries')}
              >
                <Truck className="user-profile-tab-icon" size={20} />
                <span className="user-profile-tab-text">My Deliveries</span>
              </button>
            )}
            <button
              className={`user-profile-tab-button ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <Settings className="user-profile-tab-icon" size={20} />
              <span className="user-profile-tab-text">Settings</span>
            </button>
            <button
              className={`user-profile-tab-button ${activeTab === 'addresses' ? 'active' : ''}`}
              onClick={() => setActiveTab('addresses')}
            >
              <MapPin className="user-profile-tab-icon" size={20} />
              <span className="user-profile-tab-text">Addresses</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="user-profile-tab-content">
          {activeTab === 'profile' && (
            <>
              <div className="user-profile-tab-heading-row">
                <h3>
                  <User size={24} />
                  Profile Information
                </h3>
                {!isEditing && !profileLoading && !profileError && (
                  <button className="user-profile-edit-btn" onClick={startEditing}>
                    <Edit2 size={16} />
                    Edit Profile
                  </button>
                )}
              </div>

              {profileLoading && (
                <div className="user-profile-loading">
                  <Loader size={36} className="user-profile-spinner" />
                  <p>Loading profile...</p>
                </div>
              )}

              {profileError && !profileLoading && (
                <div className="user-profile-fetch-error">
                  <AlertTriangle size={24} />
                  <p>{profileError}</p>
                  <button className="user-profile-retry-btn" onClick={fetchProfile}>Retry</button>
                </div>
              )}

              {!profileLoading && !profileError && profileData && !isEditing && (
                <div className="pi-card">
                  {/* Personal Details */}
                  <div className="pi-section">
                    <div className="pi-section-heading">
                      <User size={16} />
                      Personal Details
                    </div>
                    <div className="pi-grid">
                      <div className="pi-field">
                        <span className="pi-label">Name</span>
                        <span className="pi-value">{profileData.name || '—'}</span>
                      </div>
                      <div className="pi-field pi-field--wide">
                        <span className="pi-label">Email</span>
                        <span className="pi-value">{profileData.email}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">Phone</span>
                        <span className="pi-value">{profileData.phone || '—'}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">Gender</span>
                        <span className="pi-value">{profileData.gender || '—'}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">Date of Birth</span>
                        <span className="pi-value">{profileData.dob || '—'}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">Account Status</span>
                        <span className="pi-value">
                          {profileData.is_verified ? (
                            <span className="pi-verified-badge">
                              <CheckCircle size={14} /> Verified
                            </span>
                          ) : (
                            <span className="pi-unverified-badge">Not verified</span>
                          )}
                        </span>
                      </div>
                      <div className="pi-field pi-field--full">
                        <span className="pi-label">Roles</span>
                        <div className="pi-roles">
                          {(profileData.role ?? []).length > 0
                            ? (profileData.role ?? []).map(r => (
                                <span key={r} className="pi-role-pill">{ROLE_LABELS[r] || r}</span>
                              ))
                            : <span className="pi-value">—</span>
                          }
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pi-divider" />

                  {/* Location & Address */}
                  <div className="pi-section">
                    <div className="pi-section-heading">
                      <MapPin size={16} />
                      Location &amp; Address
                    </div>
                    <div className="pi-grid">
                      <div className="pi-field">
                        <span className="pi-label">Country</span>
                        <span className="pi-value">{profileData.country || '—'}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">State</span>
                        <span className="pi-value">{profileData.state || '—'}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">City</span>
                        <span className="pi-value">{profileData.city || '—'}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">Pincode</span>
                        <span className="pi-value">{profileData.pincode || '—'}</span>
                      </div>
                      <div className="pi-field pi-field--wide">
                        <span className="pi-label">Address</span>
                        <span className="pi-value">{profileData.address || '—'}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">Latitude</span>
                        <span className="pi-value">{profileData.latitude != null ? profileData.latitude : '—'}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">Longitude</span>
                        <span className="pi-value">{profileData.longitude != null ? profileData.longitude : '—'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="pi-divider" />

                  {/* Food Preferences */}
                  <div className="pi-section">
                    <div className="pi-section-heading">
                      <UtensilsCrossed size={16} />
                      Food Preferences
                    </div>
                    <div className="pi-grid">
                      <div className="pi-field">
                        <span className="pi-label">Food Preferences</span>
                        <span className="pi-value">{profileData.food_preferences || '—'}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">Dietary Preference</span>
                        <span className="pi-value">{profileData.dietary_preference || '—'}</span>
                      </div>
                      <div className="pi-field">
                        <span className="pi-label">Favourite Cuisine</span>
                        <span className="pi-value">{profileData.favorite_cuisine || '—'}</span>
                      </div>
                    </div>
                  </div>

                  {profileData.bio && (
                    <>
                      <div className="pi-divider" />
                      <div className="pi-bio">
                        <span className="pi-label">About Me</span>
                        <p>{profileData.bio}</p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {!profileLoading && !profileError && profileData && isEditing && (
                <form className="user-profile-edit-form" onSubmit={handleSaveProfile}>
                  <div className="user-profile-edit-photo-section">
                    <div className="user-profile-edit-avatar">
                      {(photoPreview || profileData.profile_photo) ? (
                        <img src={photoPreview || profileData.profile_photo!} alt="Profile" />
                      ) : (
                        <User size={48} />
                      )}
                    </div>
                    <button
                      type="button"
                      className="user-profile-photo-btn"
                      onClick={() => photoInputRef.current?.click()}
                    >
                      <Camera size={16} />
                      Change Photo
                    </button>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handlePhotoChange}
                    />
                  </div>

                  <div className="user-profile-edit-grid">
                    <div className="user-profile-edit-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={e => handleEditFieldChange('name', e.target.value)}
                        placeholder="Your full name"
                      />
                    </div>
                    <div className="user-profile-edit-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={editForm.phone}
                        onChange={e => handleEditFieldChange('phone', e.target.value)}
                        placeholder="Phone number"
                      />
                    </div>
                    <div className="user-profile-edit-group">
                      <label>Gender</label>
                      <select
                        value={editForm.gender}
                        onChange={e => handleEditFieldChange('gender', e.target.value)}
                      >
                        <option value="">Select gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                      </select>
                    </div>
                    <div className="user-profile-edit-group">
                      <label>Date of Birth</label>
                      <input
                        type="text"
                        value={editForm.dob}
                        onChange={e => handleEditFieldChange('dob', e.target.value)}
                        placeholder="DD-MM-YYYY"
                      />
                    </div>
                    <div className="user-profile-edit-group">
                      <label>Country</label>
                      <input
                        type="text"
                        value={editForm.country}
                        onChange={e => handleEditFieldChange('country', e.target.value)}
                        placeholder="Country"
                      />
                    </div>
                    <div className="user-profile-edit-group">
                      <label>State</label>
                      <input
                        type="text"
                        value={editForm.state}
                        onChange={e => handleEditFieldChange('state', e.target.value)}
                        placeholder="State"
                      />
                    </div>
                    <div className="user-profile-edit-group">
                      <label>City</label>
                      <input
                        type="text"
                        value={editForm.city}
                        onChange={e => handleEditFieldChange('city', e.target.value)}
                        placeholder="City"
                      />
                    </div>
                    <div className="user-profile-edit-group">
                      <label>Pincode</label>
                      <input
                        type="text"
                        value={editForm.pincode}
                        onChange={e => handleEditFieldChange('pincode', e.target.value)}
                        placeholder="Pincode"
                      />
                    </div>
                    <div className="user-profile-edit-group user-profile-edit-group--full">
                      <label>Address</label>
                      <input
                        type="text"
                        value={editForm.address}
                        onChange={e => handleEditFieldChange('address', e.target.value)}
                        placeholder="Street address"
                      />
                    </div>
                    <div className="user-profile-edit-group">
                      <label>Latitude</label>
                      <input
                        type="number"
                        step="any"
                        value={editForm.latitude}
                        onChange={e => handleEditFieldChange('latitude', e.target.value)}
                        placeholder="e.g. 8.188678"
                      />
                    </div>
                    <div className="user-profile-edit-group">
                      <label>Longitude</label>
                      <input
                        type="number"
                        step="any"
                        value={editForm.longitude}
                        onChange={e => handleEditFieldChange('longitude', e.target.value)}
                        placeholder="e.g. 77.424186"
                      />
                    </div>
                    <div className="user-profile-edit-group user-profile-edit-group--full">
                      <label>Bio</label>
                      <textarea
                        value={editForm.bio}
                        onChange={e => handleEditFieldChange('bio', e.target.value)}
                        placeholder="Tell the community about yourself..."
                        rows={3}
                      />
                    </div>
                    <div className="user-profile-edit-group">
                      <label>Food Preferences</label>
                      <input
                        type="text"
                        value={editForm.food_preferences}
                        onChange={e => handleEditFieldChange('food_preferences', e.target.value)}
                        placeholder="e.g. Organic food"
                      />
                    </div>
                    <div className="user-profile-edit-group">
                      <label>Dietary Preference</label>
                      <select
                        value={editForm.dietary_preference}
                        onChange={e => handleEditFieldChange('dietary_preference', e.target.value)}
                      >
                        <option value="">Select dietary preference</option>
                        <option value="Vegetarian">Vegetarian</option>
                        <option value="Non Vegetarian">Non Vegetarian</option>
                        <option value="Non Vegetarian & Vegetarian">Non Vegetarian &amp; Vegetarian</option>
                      </select>
                    </div>
                    <div className="user-profile-edit-group">
                      <label>Favourite Cuisine</label>
                      <input
                        type="text"
                        value={editForm.favorite_cuisine}
                        onChange={e => handleEditFieldChange('favorite_cuisine', e.target.value)}
                        placeholder="e.g. South Indian"
                      />
                    </div>
                  </div>

                  <div className="user-profile-edit-actions">
                    <button type="button" className="user-profile-cancel-btn" onClick={cancelEditing} disabled={isSaving}>
                      <X size={16} />
                      Cancel
                    </button>
                    <button type="submit" className="user-profile-save-btn" disabled={isSaving}>
                      {isSaving ? <Loader size={16} className="user-profile-spinner-sm" /> : <Save size={16} />}
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}

          {activeTab === 'active-orders' && (isCustomer || isDeliveryPartner) && (
            <>
              <h3><Activity size={24} />Active Orders</h3>
              <MyActiveOrdersTab showNotification={showNotification} />
            </>
          )}

          {activeTab === 'my-orders' && isCustomer && (
            <>
              <h3><ShoppingBag size={24} />Order History</h3>
              <UserPlacedOrdersTab showNotification={showNotification} />
            </>
          )}

          {activeTab === 'kitchen' && isKitchenOwner && (
            <>
              <h3><ChefHat size={24} />My Kitchen</h3>
              <UserKitchenManager showNotification={showNotification} />
            </>
          )}

          {activeTab === 'kitchen-orders' && isKitchenOwner && kitchenId && (
            <>
              <h3><Package size={24} />Kitchen Orders</h3>
              <MyKitchenOrdersTab kitchenId={kitchenId} showNotification={showNotification} />
            </>
          )}

          {activeTab === 'my-deliveries' && isDeliveryPartner && (
            <>
              <h3><Truck size={24} />My Deliveries</h3>
              <div className="user-profile-empty-state">
                <Truck size={60} />
                <p>Your delivery assignments will appear here once you are assigned orders to deliver.</p>
              </div>
            </>
          )}

          {activeTab === 'settings' && (
            <>
              <h3><Settings size={24} />Account Settings</h3>
              <div className="user-profile-settings-section">
                <div className="user-profile-settings-card">
                  <h4><Settings size={20} />General Settings</h4>
                  <p>Manage your account preferences and privacy settings.</p>
                  <button className="user-profile-delete-account-btn" onClick={() => setShowAccountDeletionModal(true)}>
                    <Settings size={16} />
                    Manage Account Deletion
                  </button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'addresses' && (
            <>
              <div className="user-profile-tab-heading-row">
                <h3><MapPin size={24} />Saved Addresses</h3>
                <button className="user-profile-edit-btn" onClick={() => { setShowAddressForm(v => !v); setAddressError(''); }}>
                  <Plus size={16} />
                  {showAddressForm ? 'Cancel' : 'Add Address'}
                </button>
              </div>

              {showAddressForm && (
                <form className="addr-form" onSubmit={handleSaveAddress}>
                  <div className="addr-detect-row">
                    <button
                      type="button"
                      className="addr-detect-btn"
                      onClick={handleDetectAddrLocation}
                      disabled={isDetectingAddrLocation}
                    >
                      {isDetectingAddrLocation
                        ? <><Loader size={13} className="addr-spinner" /> Detecting...</>
                        : <><Navigation size={13} /> Detect My Location</>
                      }
                    </button>
                  </div>
                  <div className="addr-form-grid">
                    <div className="addr-form-field">
                      <label>Label</label>
                      <select value={addressForm.label} onChange={e => setAddressForm(p => ({ ...p, label: e.target.value }))}>
                        <option value="Home">Home</option>
                        <option value="Work">Work</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="addr-form-field addr-form-field--full">
                      <label>Address Line</label>
                      <input type="text" required value={addressForm.address_line} onChange={e => setAddressForm(p => ({ ...p, address_line: e.target.value }))} placeholder="Street address" />
                    </div>
                    <div className="addr-form-field">
                      <label>City</label>
                      <input type="text" required value={addressForm.city} onChange={e => setAddressForm(p => ({ ...p, city: e.target.value }))} placeholder="City" />
                    </div>
                    <div className="addr-form-field">
                      <label>State</label>
                      <input type="text" value={addressForm.state} onChange={e => setAddressForm(p => ({ ...p, state: e.target.value }))} placeholder="State" />
                    </div>
                    <div className="addr-form-field">
                      <label>Pincode</label>
                      <input type="text" value={addressForm.pincode} onChange={e => setAddressForm(p => ({ ...p, pincode: e.target.value }))} placeholder="Pincode" />
                    </div>
                    <div className="addr-form-field">
                      <label>Latitude <span className="addr-optional">(optional)</span></label>
                      <input type="number" step="any" value={addressForm.latitude} onChange={e => setAddressForm(p => ({ ...p, latitude: e.target.value }))} placeholder="e.g. 8.188" />
                    </div>
                    <div className="addr-form-field">
                      <label>Longitude <span className="addr-optional">(optional)</span></label>
                      <input type="number" step="any" value={addressForm.longitude} onChange={e => setAddressForm(p => ({ ...p, longitude: e.target.value }))} placeholder="e.g. 77.424" />
                    </div>
                    <div className="addr-form-field addr-form-field--full addr-form-checkbox">
                      <label className="addr-checkbox-label">
                        <input type="checkbox" checked={addressForm.is_default} onChange={e => setAddressForm(p => ({ ...p, is_default: e.target.checked }))} />
                        Set as default address
                      </label>
                    </div>
                  </div>
                  {addressError && <p className="addr-error">{addressError}</p>}
                  <button type="submit" className="addr-save-btn" disabled={addressSaving}>
                    {addressSaving ? <><Loader size={14} className="addr-spinner" />Saving...</> : <><Save size={14} />Save Address</>}
                  </button>
                </form>
              )}

              {addressesLoading && (
                <div className="user-profile-loading">
                  <Loader size={32} className="user-profile-spinner" />
                  <p>Loading addresses...</p>
                </div>
              )}

              {!addressesLoading && savedAddresses.length === 0 && (
                <div className="user-profile-empty-state">
                  <MapPin size={48} />
                  <p>No saved addresses yet. Add one to speed up checkout.</p>
                </div>
              )}

              {!addressesLoading && savedAddresses.length > 0 && (
                <div className="addr-list">
                  {savedAddresses.map(addr => (
                    <div key={addr.address_id} className={`addr-card${activeAddressId === addr.address_id ? ' addr-card--active' : ''}`}>
                      <div className="addr-card-top">
                        <span className="addr-card-label">{addr.label.charAt(0).toUpperCase() + addr.label.slice(1)}</span>
                        {addr.is_default && <span className="addr-default-badge">Default</span>}
                        {activeAddressId === addr.address_id && <span className="addr-active-badge">Active</span>}
                      </div>
                      <p className="addr-card-line">{addr.address_line}</p>
                      <p className="addr-card-city">{[addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}</p>
                      <div className="addr-card-actions">
                        {activeAddressId !== addr.address_id && (
                          <button className="addr-btn addr-btn--set" onClick={() => handleSetActiveAddress(addr.address_id)}>
                            <CheckCircle size={13} /> Set as Active
                          </button>
                        )}
                        <button className="addr-btn addr-btn--delete" onClick={() => handleDeleteAddress(addr.address_id)}>
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showAccountDeletionModal && (
        <UserAccountDeletion onClose={() => setShowAccountDeletionModal(false)} onLogout={onLogout} />
      )}

      <div className="notification-container">
        {notifications.map(notification => (
          <div key={notification.id} className={`notification ${notification.type}`}>
            <div className="notification-icon">
              {notification.type === 'success' && <CheckCircle size={24} />}
              {notification.type === 'error' && <AlertTriangle size={24} />}
              {notification.type === 'info' && <Settings size={24} />}
              {notification.type === 'warning' && <AlertTriangle size={24} />}
            </div>
            <div className="notification-content">
              <h4 className="notification-title">{notification.title}</h4>
              <p className="notification-message">{notification.message}</p>
            </div>
            <button className="notification-close" onClick={() => removeNotification(notification.id)}>
              <X size={16} />
            </button>
          </div>
        ))}
      </div>

      {showStatusToast && (
        <div className={`kitchen-status-toast ${toastStatus}`}>
          <div className={`toast-icon ${toastStatus}`}>
            {toastStatus === 'online' ? '🟢' : '🔴'}
          </div>
          <div className="toast-content">
            <h4 className={`toast-title ${toastStatus}`}>
              Kitchen is now {toastStatus === 'online' ? 'Online' : 'Offline'}
            </h4>
            <p className="toast-message">
              {toastStatus === 'online'
                ? 'Your kitchen is open and ready to receive orders!'
                : 'Your kitchen is closed and not accepting new orders.'}
            </p>
          </div>
          <button className="toast-close" onClick={() => setShowStatusToast(false)}>
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
